/**
 * Money helpers for integer minor units (finance target: utils/currency.ts,
 * lifted from the AdsView currencyFormatter). Amounts never travel as floats.
 */

const formatters = new Map<string, Intl.NumberFormat>();

function formatterFor(currency: string): Intl.NumberFormat {
  let f = formatters.get(currency);
  if (!f) {
    // it-IT skips grouping below 10.000 by default. An explicit `true` means "always" (Intl
    // NumberFormat v3), so 5.950,00 € lines up with 10.900,00 €. The string form needs TS 5.
    f = new Intl.NumberFormat("it-IT", { style: "currency", currency, useGrouping: true });
    formatters.set(currency, f);
  }
  return f;
}

/** Format minor units as currency, Italian locale: 49000, "EUR" -> "490,00 €". */
export function formatMoney(amountMinor: number, currency: string): string {
  // `|| 0` folds -0 into 0 so a zero never renders as "-0,00 €".
  return formatterFor(currency).format((amountMinor || 0) / 100);
}

/**
 * Parse user input into minor units. Accepts "1.234,56" (it-IT), "5.950"
 * (dots grouping thousands) and "1234.56". Returns null for empty or
 * unparseable input. String math, so no float drift.
 */
export function parseMoneyMinor(text: string): number | null {
  const t = text.trim().replace(/[\s€£$]/g, "");
  if (!t) return null;
  const thousandsOnly = /^-?\d{1,3}(\.\d{3})+$/.test(t);
  const normalized = t.includes(",") || thousandsOnly ? t.replace(/\./g, "").replace(",", ".") : t;
  const m = /^(-?)(\d+)(?:\.(\d{0,2}))?$/.exec(normalized);
  if (!m) return null;
  const minor = Number(m[2]) * 100 + Number((m[3] ?? "").padEnd(2, "0"));
  return m[1] ? -minor : minor;
}

/** Minor units to an editable string: 49000 -> "490,00". */
export function minorToInput(amountMinor: number | null): string {
  return amountMinor === null ? "" : (amountMinor / 100).toFixed(2).replace(".", ",");
}
